import Vue from 'vue'
import 'vue-multiselect/dist/vue-multiselect.min.css'
// tailwind
import './assets/index.css'
import './utils/gtm'
import { amplitudeInit } from './utils/amplitude'
import { ACTIVITY_ACTIONS } from './constants'

import ElementUI, { Message } from 'element-ui'
import locale from 'element-ui/lib/locale/lang/en' // lang i18n
import './assets/element-variables.scss'
import Vuelidate from 'vuelidate'

// set ElementUI lang to EN
Vue.use(ElementUI, { locale })

import './assets/styles/main.sass'
import '@/styles/index.scss' // global css

import App from './App.vue'
import router from './router'

import { formatMoney, projectSettings } from './utils/utils'

import Vuebar from 'vuebar'
import store from './store'

import LoadScript from 'vue-plugin-load-script'
import 'element-closest-polyfill'
import VueLazyload from 'vue-lazyload'

import VueMeta from 'vue-meta'
import * as Sentry from '@sentry/vue'
import LaunchDarkly from './utils/launch-darkly-initialize'

const moment = require('moment-timezone')

import 'vue-datetime/dist/vue-datetime.css'

amplitudeInit()
const launchDarkly = LaunchDarkly.deferInitialization(process.env.VUE_APP_LAUNCHDARKLY_CLIENT_ID)

Vue.prototype.$launchDarkly = launchDarkly
launchDarkly.initialize({
  kind: 'user',
  key: 'anonymous',
  anonymous: true,
})
const ldClientPromise = launchDarkly.getClient()
ldClientPromise.then((client) => {
  store.dispatch('featureFlags/bindLaunchDarklyClient', client)
})

import config from '../public/harness_config.json'

import auth from '@/auth/authService'

require('./icons.js')
require('./toasts.js')

import VueTippy, { TippyComponent } from 'vue-tippy'
Vue.component('Tippy', TippyComponent)

import Popover from 'vue-js-popover'
Vue.use(Popover)

import CKEditor from '@ckeditor/ckeditor5-vue2'
Vue.use(CKEditor)

import VModal from 'vue-js-modal'

import './assets/element-variables.scss'

Vue.use(VModal, { componentName: 'v-modal', dialog: true })

Vue.use(VueTippy, {
  delay: 100,
  arrow: true,
  arrowType: 'sharp',
  size: 'regular',
  duration: 200,
  maxWidth: 250,
  animation: 'shift-away',

  animateFill: true,
  theme: 'dark',

  // How far the tooltip is from its reference element in pixels
  distance: 10,
})

// Vue.use(V2Table);

Vue.use(VueLazyload)
Vue.use(VueMeta)
Vue.use(Vuebar)
Vue.use(LoadScript)
String.prototype.replaceAll = function (search, replacement) {
  const target = this
  return target.split(search).join(replacement)
}

Vue.use(Vuelidate)

Vue.config.productionTip = false

Vue.filter('shortName', function (name) {
  return (name.fname ? name.fname[0] : '') + (name.lname ? name.lname[0] : '')
})

Vue.filter(
  'webAppLink',
  (link, charityInfo) => `${config.scheme}://${charityInfo.subdomain}.${config.url}/wv2${link}`
)

Vue.filter(
  'newWebAppLink',
  (link, charityInfo) =>
    `${config.donor_next_scheme}://${charityInfo.subdomain}.${config.donor_next_url}${link}`
)

Vue.filter('prepareName', function (name) {
  if (!name) {
    return ''
  }

  return (name.fname ? name.fname + ' ' : '') + (name.lname ? name.lname : '')
})

Vue.filter('dateFormat', function (date, format, fromFormat = false) {
  if (date === null) {
    return moment().format(format)
  }
  if (fromFormat) {
    return moment(date, fromFormat).format(format)
  }
  return moment(date).format(format)
})

Vue.filter('dateFormatTz', function (date, format, fromTz) {
  fromTz = fromTz ? fromTz : 'UTC'
  return convertTimezone(date, fromTz, format)
})

function convertTimezone(sentDateTime, fromTimezone, format) {
  if (sentDateTime) {
    const currentDate = moment.tz(sentDateTime, fromTimezone)
    const zoneName = moment.tz.guess()
    const convertedDate = currentDate.clone().tz(zoneName)
    return convertedDate.format(format)
  }
  return 'N/A'
}

Vue.filter('formatMoney', function (amount, n = 0, x = 3) {
  return formatMoney(amount, n, x)
})

Vue.filter('activityTitle', function (action) {
  const activityTitles = {
    [ACTIVITY_ACTIONS.ROUNDUP]: 'Round-Up',
    [ACTIVITY_ACTIONS.CUSTOM]: 'Donation',
    [ACTIVITY_ACTIONS.RECURRING]: 'Monthly',
    [ACTIVITY_ACTIONS.SIGNUP]: 'NEW Contact',
    [ACTIVITY_ACTIONS.MONTHLY_DONATION_CREATED]: 'NEW Monthly Gift',
    [ACTIVITY_ACTIONS.ROUNDUP_ACCOUNT_CREATED]: 'NEW Round-Up Donor',
    [ACTIVITY_ACTIONS.TICKET_SOLD]: 'Ticket(s) sold',
  }

  return activityTitles[action] || action
})

Vue.filter('ucwords', function (str) {
  return (str + '').replace(/^([a-z])|\s+([a-z])/g, function ($1) {
    return $1.toUpperCase()
  })
})

Vue.filter('sinceTime', function (timestamp, dateNow) {
  return moment(timestamp).from(dateNow).replace('ago', '')
})

router.beforeEach((to, from, next) => {
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const requiresNoAuth = to.matched.some((record) => record.meta.requiresNoAuth)
  const noIntercom = to.matched.some((record) => record.meta.noIntercom)
  // There may be multiple `matched` RouteRecords (for nested routes),
  // so get ALL feature flags that could gate this route.
  const featureFlagRecords = to.matched.filter(
    (record) =>
      record.meta.featureFlagKey ||
      (record.meta.featureFlagKeysAny && record.meta.featureFlagKeysAny.length)
  )

  if (window.analytics) {
    window.analytics.page(`Visited ${to.name}`, {
      charity_id:
        auth.isAuthenticated() && store.state.auth.activeCharityId
          ? store.state.auth.activeCharityId
          : '',
      mobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ),
      browser: navigator.userAgent,
    })
  }

  if (noIntercom) {
    document.querySelector('body').classList.add('no-intercom')
  } else {
    document.querySelector('body').classList.remove('no-intercom')
  }

  if (requiresAuth && !auth.isAuthenticated()) {
    next({ path: '/admin-login.html', query: { r: to.fullPath } })
  } else if (requiresNoAuth && auth.isAuthenticated()) {
    next('/dashboard')
  }

  if (auth.isAuthenticated() && store.state.auth.activeCharityId) {
    if (store.state.donations.donationsState === 'ready') {
      store.dispatch('donations/fetchAllDonations', { charityId: store.state.auth.activeCharityId })
    }
    if (store.state.donors.donorsState === 'ready') {
      store.dispatch('donors/fetchAllDonors', { charityId: store.state.auth.activeCharityId })
    }
  }

  // Route meta feature flags — evaluated from Vuex (kept in sync with LaunchDarkly listeners).
  if (featureFlagRecords.length) {
    store
      .dispatch('featureFlags/ensureReady')
      .then(() => {
        const featureFlagEvaluation = featureFlagRecords.every((record) =>
          store.getters['featureFlags/evaluateRouteRecord'](record)
        )

        if (featureFlagEvaluation) {
          next()
        } else {
          const fallback = featureFlagRecords.find((record) => record.meta?.featureFlagFallback)
            ?.meta?.featureFlagFallback
          if (fallback) {
            const location = { ...fallback, query: to.query }
            if (fallback.name) {
              location.params = to.params
            } else if (fallback.path && to.params) {
              location.path = Object.entries(to.params).reduce(
                (resolvedPath, [key, value]) => resolvedPath.replace(`:${key}`, value),
                fallback.path
              )
            }
            next(location)
          } else {
            next(false)
          }
        }
      })
      .catch((error) => {
        Message.error('Unable to load this page. Please refresh and try again.')
        if (config.sentry?.dsn) {
          Sentry.captureException(error)
        }
        next(false)
      })
    // Since the above is async, we need to return here to avoid calling the `next()` below.
    // https://v3.router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
    return
  }

  next()
})
import './components/Settings/settings.sass'
import './components/Fundraise/giving-forms.scss'

// eslint-disable-next-line no-undef
const gitHash = typeof __APP_GIT_HASH__ === 'string' ? __APP_GIT_HASH__ : null

if (config.sentry && config.sentry.dsn) {
  // Sentry release constant defined in `vite.config.js`.
  const definedReleaseId =
    // eslint-disable-next-line no-undef
    typeof __HARNESS_SENTRY_RELEASE__ === 'string' ? __HARNESS_SENTRY_RELEASE__ : null

  // Attempt to parse the sample rate value, if one is configured.
  const parsedSampleRate =
    config.sentry.sample_rate !== undefined ? parseFloat(config.sentry.sample_rate) : null

  const sampleRate =
    parsedSampleRate !== null &&
    !isNaN(parsedSampleRate) &&
    parsedSampleRate >= 0.0 &&
    parsedSampleRate <= 1.0
      ? parsedSampleRate
      : 1.0

  // List of User-Agents for which we do not want to send any Sentry events.
  const blockedUserAgents = ['HeadlessChrome', 'prerender/prerender']

  // For a given `event` (or transaction), check if the User-Agent is one which is blocked.
  const isBlockedUserAgent = (event) => {
    return (
      blockedUserAgents.findIndex((userAgent) => {
        return (
          window.navigator.userAgent.search(userAgent) !== -1 ||
          (event &&
            event.request &&
            event.request.headers &&
            event.request.headers['User-Agent'].search(userAgent) !== -1)
        )
      }) !== -1
    )
  }

  Sentry.init({
    Vue,
    dsn: config.sentry.dsn,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
    ],
    sampleRate: sampleRate,
    tracesSampleRate: sampleRate,
    environment: config.sentry.ENV || config.ENV || 'development',
    release: definedReleaseId || gitHash || undefined,
    beforeSend(event) {
      if (isBlockedUserAgent(event)) {
        return null
      }
      return event
    },
    beforeSendTransaction(event) {
      if (isBlockedUserAgent(event)) {
        return null
      }
      return event
    },
    denyUrls: [/www\.googletagmanager\.com/], // ignore "Cannot read properties of null (reading 'first_name')" error
    ignoreErrors: [
      // Error generated by Microsoft SafeLinks crawler
      // See: https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/20
      'Non-Error promise rejection captured with value: Object Not Found Matching Id:',
    ],
  })
}

new Vue({
  el: '#app',
  store,
  router,
  data() {
    return {
      mergeTags: [],
      user: null,
      charity_id: '',
      charities: [],
      canva: null,
    }
  },
  created() {
    Vue.loadScript('https://sdk.canva.com/v2/beta/api.js').then(() => {
      ;(async () => {
        if (window.CanvaButton) {
          this.canva = await window.CanvaButton.initialize({
            apiKey: 'dGVB5fnxxOGP8iyq1a6E--zJ',
          })
        }
      })()
    })
  },
  methods: {
    checkFeature(feature) {
      return projectSettings.isAllowed(feature)
    },
    canAddCampaign() {
      return projectSettings.canAddCampaign()
    },
    isLogin() {
      return auth.isAuthenticated()
    },
  },
  render: (h) => h(App),
})
